import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { ARTIST_NAME, IMG, MUSICIANS, VIDEOS } from "@/data/content";
import {
  Reveal, ChapterLabel, PageHero, ParallaxImage, CTALink, BookingSection, VideoLightbox, usePageTitle, ImageReveal,
} from "@/components/Shared";
import { Play } from "lucide-react";

const QuartettPage = () => {
  const { t, lp, lf } = useLang();
  const [lightbox, setLightbox] = useState(null);
  usePageTitle(`${t.nav.quartett} — ${ARTIST_NAME}`);
  const video = VIDEOS.find((v) => v.id === "caroussel-resonance");
  const gallery = [IMG.quartett, IMG.piano, IMG.concert2, IMG.saxGold];

  return (
    <div data-testid="quartett-page">
      <PageHero eyebrow={t.quartett.eyebrow} title={t.quartett.title} img={IMG.quartett} />

      {/* About */}
      <section className="px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal><ChapterLabel no="01" label={t.quartett.aboutLabel} /></Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal><p className="font-serif text-2xl font-light italic leading-relaxed text-[#F9F8F6] sm:text-3xl">{t.quartett.aboutText}</p></Reveal>
            <Reveal delay={0.15}><p className="mt-8 font-sans text-sm leading-loose text-[#A1A1AA]">{t.quartett.soundText}</p></Reveal>
          </div>
        </div>
      </section>

      {/* Musicians — editorial portrait modules */}
      <section className="border-y border-white/5 bg-[#141416] px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <Reveal><ChapterLabel no="02" label={t.quartett.musiciansLabel} /></Reveal>
        <div className="mt-12 grid gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
          {MUSICIANS.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.1} className={i % 2 === 1 ? "sm:mt-12" : ""}>
              <div data-testid={`musician-${m.id}`} className="group">
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img src={m.img} alt={m.name} loading="lazy" className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C]/80 to-transparent" />
                  <div className="absolute bottom-0 p-5">
                    <p className="font-serif text-xl font-light text-[#F9F8F6]">{m.name}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{lf(m.role)}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Sound */}
      <section className="relative overflow-hidden">
        <ParallaxImage src={IMG.stage2} alt="" className="absolute inset-0 h-full" imgClass="opacity-20" speed={40} />
        <div className="relative z-10 px-6 py-24 text-center sm:px-12 sm:py-32">
          <Reveal><ChapterLabel no="03" label={t.quartett.soundLabel} /></Reveal>
          <Reveal delay={0.15}>
            <p className="mx-auto max-w-3xl font-serif text-xl font-light leading-relaxed text-[#F9F8F6] sm:text-2xl">{t.quartett.soundText}</p>
          </Reveal>
        </div>
      </section>

      {/* Live + Gallery */}
      <section className="px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <Reveal><ChapterLabel no="04" label={t.quartett.liveLabel} /></Reveal>
        <Reveal delay={0.1}><p className="max-w-2xl font-sans text-sm leading-loose text-[#A1A1AA]">{t.quartett.liveText}</p></Reveal>
        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {gallery.map((g, i) => (
            <div key={g} data-cursor="VIEW" className={i % 2 === 1 ? "sm:mt-10" : ""}>
              <ImageReveal src={g} alt={`Angela Puxi Quartett live ${i + 1}`} bw={i % 2 === 0} delay={i * 0.07} className="aspect-[3/4]" />
            </div>
          ))}
        </div>
      </section>

      {/* Video */}
      <section className="px-6 pb-24 sm:px-12 sm:pb-32 lg:px-20">
        <Reveal><ChapterLabel no="05" label={t.quartett.videoLabel} /></Reveal>
        <button
          data-testid="quartett-video-teaser"
          data-cursor="PLAY"
          onClick={() => setLightbox(video)}
          className="group relative mt-8 block aspect-video w-full overflow-hidden"
        >
          <img src={video.img} alt={video.title} loading="lazy" className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#C5A059]/60 bg-[#0B0B0C]/60 backdrop-blur transition-all duration-500 group-hover:bg-[#C5A059]">
              <Play className="ml-1 h-5 w-5 text-[#C5A059] group-hover:text-[#0B0B0C]" />
            </span>
          </div>
        </button>
      </section>

      <section className="px-6 pb-24 text-center sm:px-12">
        <Reveal>
          <ChapterLabel no="06" label={t.quartett.bookingLabel} />
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CTALink to={lp("/contact")} testid="quartett-booking-cta">{t.common.booking}</CTALink>
            <CTALink to={lp("/bands")} testid="quartett-back-link" variant="outline">{t.common.backToBands}</CTALink>
          </div>
        </Reveal>
      </section>

      <BookingSection />
      <AnimatePresence>{lightbox && <VideoLightbox item={lightbox} onClose={() => setLightbox(null)} />}</AnimatePresence>
    </div>
  );
};

export default QuartettPage;
